class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.isConnecting = false;
    this.isConnected = false;
    this.connectionUrl = null;
    this.channels = new Set(); // Track subscribed channels
    this.messageQueue = []; // Queue messages when disconnected
  }

  connect(url) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    this.connectionUrl = url;

    try {
      // Get auth token if available
      let token = null;
      try {
        // Try to get token from Vue cookies first, then localStorage
        if (typeof window !== 'undefined' && window.VueCookies) {
          token = window.VueCookies.get('access_token');
        } else if (typeof document !== 'undefined') {
          // Fallback to manual cookie parsing
          const cookies = document.cookie.split(';');
          const accessTokenCookie = cookies.find(cookie => cookie.trim().startsWith('access_token='));
          if (accessTokenCookie) {
            token = accessTokenCookie.split('=')[1];
          }
        }
        
        // Fallback to localStorage
        if (!token && typeof localStorage !== 'undefined') {
          token = localStorage.getItem('access_token');
        }
      } catch (error) {
        console.warn('Could not retrieve auth token:', error);
      }
      
      // Create WebSocket connection with auth token in URL or headers
      const wsUrl = token ? `${url}?token=${token}` : url;
      this.socket = new WebSocket(wsUrl);
      console.log('WebSocket connection URL:', wsUrl);
      this.socket.onopen = (event) => {
        console.log('WebSocket connected successfully:', event);
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Re-subscribe to channels
        this.resubscribeChannels();
        
        // Send queued messages
        this.flushMessageQueue();
        
        this.emit('connected', event);
      };

      this.socket.onmessage = (event) => {
        try {
          // Handle potential batched messages (multiple JSON objects separated by newlines)
          const messageData = event.data;
          
          // Split by newlines to handle batched messages
          const messages = messageData.split('\n').filter(msg => msg.trim());
          
          messages.forEach(msg => {
            try {
              if (msg.trim()) {
                const data = JSON.parse(msg.trim());
                console.log('WebSocket message received:', data);
                
                // Handle different message types
                this.handleMessage(data);
              }
            } catch (parseError) {
              console.warn('Error parsing individual WebSocket message:', parseError, 'Message:', msg);
              // Continue processing other messages in the batch
            }
          });
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          console.error('Raw message data:', event.data);
          this.emit('error', { type: 'parse_error', error });
        }
      };

      this.socket.onclose = (event) => {
        console.log('WebSocket connection closed:', event);
        this.isConnecting = false;
        this.isConnected = false;
        this.emit('disconnected', event);
        
        // Attempt to reconnect if not manually closed
        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
          console.log(`WebSocket connection lost, attempting to reconnect (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
          this.scheduleReconnect();
        } else if (event.wasClean) {
          console.log('WebSocket connection closed cleanly');
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        this.isConnecting = false;
        this.emit('error', error);
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('error', error);
    }
  }

  scheduleReconnect() {
    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectInterval}ms`);
    
    setTimeout(() => {
      if (this.reconnectAttempts <= this.maxReconnectAttempts && this.connectionUrl) {
        this.connect(this.connectionUrl);
      }
    }, this.reconnectInterval);
  }

  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    this.isConnected = false;
    this.connectionUrl = null;
    this.channels.clear();
    this.messageQueue = [];
    this.listeners.clear();
  }

  // Close the current socket WITHOUT removing registered listeners or config.
  // Used by reconnect()/logout() so plugin-level listeners survive across logins.
  _closeSocket(reason) {
    if (this.socket) {
      try {
        // Detach handlers so this manual close doesn't trigger a reconnect
        // or emit a spurious 'disconnected' for the stale socket.
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.close(1000, reason || 'closing');
      } catch (error) {
        console.warn('Error closing WebSocket:', error);
      }
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
  }

  // Re-open the connection using the LATEST auth token. Call this right after
  // login: the socket was first opened at app start (before login) and would
  // otherwise stay connected anonymously until a full page refresh.
  reconnect() {
    this._closeSocket('reconnecting with new auth');
    this.reconnectAttempts = 0;
    const url = this.connectionUrl || process.env.VUE_APP_WS_URL;
    return this.connect(url);
  }

  // Tear down the connection on logout while keeping listeners intact, so the
  // socket stops carrying the previous user's identity. The next login will
  // call reconnect() and open a fresh, authenticated connection.
  logout() {
    this.channels.clear();
    this.messageQueue = [];
    this._closeSocket('logout');
  }

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = JSON.stringify(data);
      console.log('WebSocket sending message:', message);
      this.socket.send(message);
    } else {
      console.warn('WebSocket is not connected. Queueing message:', data);
      this.messageQueue.push(data);
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        this.listeners.delete(event);
      }
    }
  }

  emit(event, data) {
    console.log(`WebSocket: Emitting event: ${event}`, data);
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      console.log(`WebSocket: Found ${callbacks.length} listeners for event: ${event}`);
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket event handler for '${event}':`, error);
        }
      });
    } else {
      console.log(`WebSocket: No listeners found for event: ${event}`);
    }
  }

  // Channel subscription methods
  subscribe(channel) {
    console.log(`WebSocket: Subscribing to channel: ${channel}`);
    this.channels.add(channel);
    console.log(`WebSocket: Current subscribed channels:`, Array.from(this.channels));
    this.send({
      type: 'subscribe',
      channel: channel
    });
  }

  unsubscribe(channel) {
    console.log(`WebSocket: Unsubscribing from channel: ${channel}`);
    this.channels.delete(channel);
    this.send({
      type: 'unsubscribe',
      channel: channel
    });
  }

  resubscribeChannels() {
    this.channels.forEach(channel => {
      this.send({
        type: 'subscribe',
        channel: channel
      });
    });
  }

  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  handleMessage(data) {
    try {
      console.log('WebSocket service handling message:', data);
      
      // Emit channel-specific events if channel is present
      if (data.channel) {
        this.emit(`${data.channel}:${data.type}`, data);
        this.emit(`${data.channel}:message`, data);
      }
      
      // Emit type-specific events (this is what the backend currently sends)
      if (data.type) {
        this.emit(data.type, data);
        
        // Also emit channel-specific events for loads since we're subscribed to that channel
        if (this.channels.has('loads')) {
          this.emit(`loads:${data.type}`, data);
        }
      }
      
      // Emit general message event
      this.emit('message', data);
    } catch (error) {
      console.error('Error handling WebSocket message:', error, 'Message data:', data);
    }
  }

  // Convenience methods for common channels
  subscribeToLoads() {
    this.subscribe('loads');
  }

  subscribeToUsers() {
    this.subscribe('users');
  }

  subscribeToNotifications() {
    this.subscribe('notifications');
  }

  subscribeToSystem() {
    this.subscribe('system');
  }

  // Listen to specific channel events
  onChannel(channel, eventType, callback) {
    this.on(`${channel}:${eventType}`, callback);
  }

  offChannel(channel, eventType, callback) {
    this.off(`${channel}:${eventType}`, callback);
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  getConnectionStatus() {
    if (!this.socket) return 'CLOSED';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  // Get subscribed channels
  getSubscribedChannels() {
    return Array.from(this.channels);
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
